import React from 'react'
import cn from 'classnames'
import styled from '@emotion/styled'
import { Form, Input } from 'util/ui'
import { propFunc } from 'util/functions'
import { times } from 'util/arrays'
import { splice } from 'util/strings'
import { disableLastPassInputRef } from 'util/disableLastPassInputRef'
import FieldError from 'shared/components/ui/FieldError'
import SpacedItems from 'shared/components/ui/SpacedItems'
import themeVars from 'ui_theme/site/globals/site.variables'

import PageHeader from '../PageHeader'

import sharedStyles from '../../sharedForm/styles.less'
import SaveIndicator from '../../sharedForm/SaveIndicator'
import EmailForm from '../../sharedForm/EmailForm'
import ResendToken from '../../sharedForm/ResendToken'

const EXPECTED_CODE_LENGTH = 6
const BLANK_DIGIT = '\u200B'
const EMPTY_CODE = ''.padStart(EXPECTED_CODE_LENGTH, BLANK_DIGIT)
const CenteredField = styled(Form.Field)`
  text-align: center;
`

const Digits = styled('div')`
  display: flex;
  align-items: center;
  align-content: center;
  justify-content: center;
`

const DigitInput = styled(Input)`
  width: 48px !important;
  height: 48px !important;
  margin-left: ${props => props.theme.spacing['5']};
  &:first-child {
    margin-left: 0;
  }
  & > input {
    padding: ${props => props.theme.spacing['3']} !important;
    text-align: center !important;
  }
  &.error input {
    border: 1px solid ${props => props.theme.color.primary.negative} !important;
  }
`

const LargeFieldError = styled(FieldError)`
  margin-top: ${props => props.theme.spacing['10']};
  font-size: ${themeVars.fontSize} !important;
  &&& {
    font-style: normal !important;
  }
  & > .icon {
    font-size: ${themeVars['18px']} !important;
    line-height: ${themeVars['15px']};
    vertical-align: middle;
  }
`

class VerificationPage extends React.PureComponent {
  // eslint-disable-next-line react/sort-comp
  state = {
    prevCode: EMPTY_CODE,
    code: EMPTY_CODE,
    errorDisplayable: false,
    lastFocusedDigit: null,
  }

  constructor(props) {
    super(props)
    times(EXPECTED_CODE_LENGTH, i => {
      this[`digitRef${i}`] = React.createRef()
    })
  }

  componentDidMount() {
    times(EXPECTED_CODE_LENGTH, i => {
      const digitRef = this[`digitRef${i}`]
      const currentRef = digitRef && digitRef.current
      if (currentRef) {
        disableLastPassInputRef(currentRef)
      }
    })
  }

  getInputRef(position) {
    return this[`digitRef${position}`].current.inputRef.current
  }

  handleFocus = position => {
    const { code } = this.state
    const newCode = splice(code, position, 1, BLANK_DIGIT)
    this.setState({
      prevCode: code,
      code: newCode,
      lastFocusedDigit: null,
    })
  }

  handleBlur = position => {
    const { prevCode, code } = this.state
    const parsedDigit = (code[position] || '').replace(/[^0-9]/g, '')
    if (parsedDigit === '') {
      const newCode = splice(code, position, 1, prevCode[position] || '')
      this.setState({
        prevCode: EMPTY_CODE,
        code: newCode,
        lastFocusedDigit: position,
      })
    } else {
      this.setState({ prevCode: code })
    }
  }

  handleDigit = (position, _, { value }) => {
    // NOTE (jscheel): This handles both single digit entry a well as pasting.
    const parsedInput = value.replace(/[^0-9]/g, '')
    if (parsedInput.length === 0) return
    const newCode = splice(
      this.state.code,
      position,
      parsedInput.length,
      parsedInput
    ).substring(0, EXPECTED_CODE_LENGTH)
    this.setState(
      {
        prevCode: newCode,
        code: newCode,
        errorDisplayable: false,
      },
      () => {
        const nextPosition = position + parsedInput.length
        if (nextPosition >= EXPECTED_CODE_LENGTH) {
          this.getInputRef(position).blur()
        } else {
          this.getInputRef(nextPosition).focus()
        }
        this.props.onChange('confirmationToken', newCode, null, {
          validate: true,
        })
      }
    )
  }

  handleSubmit = (...args) => {
    this.setState({ errorDisplayable: true })
    this.props.submitForm(...args)
  }

  handleEmailFormClose = () => {
    const { lastFocusedDigit } = this.state
    if (lastFocusedDigit !== null) {
      this[`digitRef${lastFocusedDigit}`].current.focus()
    }
  }

  handleEmailFormSubmit = () => {
    this.setState({
      prevCode: EMPTY_CODE,
      code: EMPTY_CODE,
    })
    this.digitRef0.current.focus()
  }

  render() {
    const { formFieldsErrors, canSave, saving, email } = this.props
    const error =
      this.state.errorDisplayable && formFieldsErrors.confirmationToken
    return (
      <React.Fragment>
        <PageHeader
          primary="Verify your email address"
          secondary={
            <React.Fragment>
              A 6 digit code has been sent to{' '}
              <strong>{email || 'unknown'}</strong> used to verify your address
            </React.Fragment>
          }
        />
        <Form
          size="large"
          className={cn(sharedStyles.form, sharedStyles.formV2)}
        >
          <CenteredField>
            <label htmlFor="code">Place in your 6 digit code below</label>
            <Digits>
              {times(EXPECTED_CODE_LENGTH, position => (
                <DigitInput
                  name={`digit_${position}`}
                  key={position}
                  value={this.state.code[position] || ''}
                  onFocus={propFunc(this.handleFocus, position)}
                  onBlur={propFunc(this.handleBlur, position)}
                  onChange={propFunc(this.handleDigit, position)}
                  autoFocus={position === 0}
                  error={!!error}
                  ref={this[`digitRef${position}`]}
                />
              ))}
            </Digits>
            {error && <LargeFieldError as="div">{error}</LargeFieldError>}
          </CenteredField>
          <Form.Button
            primary
            size="big"
            fluid
            disabled={!canSave}
            onClick={this.handleSubmit}
          >
            {!saving ? (
              'Continue'
            ) : (
              <SaveIndicator animate>Saving</SaveIndicator>
            )}
          </Form.Button>
          <Form.Field>
            <SpacedItems.Container horizontalAlign="space-between">
              <SpacedItems.Item>
                <EmailForm
                  onSubmit={this.handleEmailFormSubmit}
                  onClose={this.handleEmailFormClose}
                />
              </SpacedItems.Item>
              <SpacedItems.Item>
                <ResendToken />
              </SpacedItems.Item>
            </SpacedItems.Container>
          </Form.Field>
        </Form>
      </React.Fragment>
    )
  }
}

export default VerificationPage
